import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
} from "react";
import { User, Clock, Shield, Briefcase, MapPin } from "react-feather";

// Shared Components
import LoadingSpinner from "./LoadingSpinner";
import Input from "./input/Input";
import Dropdown from "./input/Dropdown";
import SearchDropdown from "./input/SearchDropdown";
import CheckBox from "./input/CheckBox";
import InfoList from "./InfoList";

// APIs & Utilities
import { gsDivStartsWith } from "../api/gsApi";
import { policeDivStartsWith } from "../api/policeDivisionApi";
import { getOtherRefDetails, getOtherRefSearchDropdown } from "../api/keyvalue";
import { employeeValidation } from "./validations/EmployeeCreation";
import {
  calculateMedicalExpiryDate,
  isMedicalCertificateValid,
} from "./validations/documentRequirementHelper";
import { formatFromJsonDate } from "../dateConfig";
import { FORMS, MODULES } from "../const";
import useUserCompany from "../hooks/useUserCompany";
import { validateFieldUserName } from "../api/employeeApi";

const FORM = FORMS.EMPLOYEE.EMP_DETAIL.form;

const EmployeeComponent = forwardRef(
  ({ errors = {}, dropdown = {}, isLoading, currentValues, editMode }, ref) => {
    // --- Context & Local Hooks ---
    const userCompany = useUserCompany();
    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : null,
    );
    const [currentErrors, setCurrentErrors] = useState(errors);
    const [stakeholderType, setStakeholderType] = useState("");
    const [age, setAge] = useState("");
    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : null);
    }, [userCompany]);

    useEffect(() => {
      setCurrentErrors(errors || {});
    }, [errors]);

    const [initialActive, setInitialActive] = useState(
      editMode ? currentValues?.empActive : 1,
    );

    // --- Helper Functions ---
    const getEndLocationId = useCallback((loc) => {
      if (!loc) return null;
      return typeof loc === "object" ? loc.locId || loc.id : loc;
    }, []);

    const calculateAge = useCallback((birthDay) => {
      if (!birthDay) return "";
      const today = new Date();
      const birthDate = new Date(birthDay);
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        calculatedAge--;
      }
      return calculatedAge < 0 ? 0 : calculatedAge;
    }, []);

    // --- Default Form State ---
    const defaultState = useMemo(
      () => ({
        empCode: "",
        empReference: "",
        empFullName: "",
        empCommonName: "",
        empNic: "",
        empCategory: "",
        empLicenceNumber: "",
        empExpiryDate: "",
        empBirthDate: "",
        empMedicalIssueDate: "",
        empMedicalExpiryDate: "",
        empStakeholder: null,
        empGsDivision: "",
        empGsExpiry: "",
        empPoliceDivision: "",
        empPoliceExpiry: "",
        empAuthorized: "",
        empAvc: "",
        empAvcExpiry: "",
        empRemarks: "",
        empLastInspection: "",
        empActive: 1,
        empStatus: 2,
        empPhone: "",
        empMobile: "",
        empImage: "",
        empEmail: "",
        empCompany: company,
        empUserEnable: false,
        empUserName: "",
        empInsuranceCoverage: "",
        empInsuranceExpDate: "",
        empInsuranceProvider: "",
        empShiftStart: "08:00",
        empShiftEnd: "17:00",
        empRouteEndType: "depot",
        empEndLocation: null,
      }),
      [company],
    );

    const [employeeData, setEmployeeData] = useState(() => {
      if (editMode && currentValues) {
        return {
          ...defaultState,
          ...currentValues,
          empStakeholder: currentValues.empStakeholderId,
          empExpiryDate: currentValues.empExpiryDate
            ? currentValues.empExpiryDate.split("T")[0]
            : "",
          empBirthDate: currentValues.empBirthDate
            ? currentValues.empBirthDate.split("T")[0]
            : "",
          empGsExpiry: currentValues.empGsExpiry
            ? currentValues.empGsExpiry.split("T")[0]
            : "",
          empPoliceExpiry: currentValues.empPoliceExpiry
            ? currentValues.empPoliceExpiry.split("T")[0]
            : "",
          empAvcExpiry: currentValues.empAvcExpiry
            ? currentValues.empAvcExpiry.split("T")[0]
            : "",
          empLastInspection: currentValues.empLastInspection
            ? currentValues.empLastInspection.split("T")[0]
            : "",
          empInsuranceExpDate: currentValues.empInsuranceExpDate
            ? currentValues.empInsuranceExpDate.split("T")[0]
            : "",
          empMedicalIssueDate: currentValues.empMedicalIssueDate
            ? currentValues.empMedicalIssueDate.split("T")[0]
            : "",
          empMedicalExpiryDate: currentValues.empMedicalExpiryDate
            ? currentValues.empMedicalExpiryDate.split("T")[0]
            : currentValues.empMedicalIssueDate
              ? calculateMedicalExpiryDate(currentValues.empMedicalIssueDate)
              : "",
          empShiftStart: currentValues.empShiftStart || "08:00",
          empShiftEnd: currentValues.empShiftEnd || "17:00",
          empRouteEndType: currentValues.empRouteEndType || "depot",
          empEndLocation: getEndLocationId(currentValues.empEndLocation),
        };
      }
      return defaultState;
    });

    // Sync Edit Mode changes
    useEffect(() => {
      if (editMode && currentValues) {
        setInitialActive(currentValues.empActive);
        setEmployeeData({
          ...defaultState,
          ...currentValues,
          empStakeholder: currentValues.empStakeholderId,
          empExpiryDate: currentValues.empExpiryDate
            ? currentValues.empExpiryDate.split("T")[0]
            : "",
          empBirthDate: currentValues.empBirthDate
            ? currentValues.empBirthDate.split("T")[0]
            : "",
          empGsExpiry: currentValues.empGsExpiry
            ? currentValues.empGsExpiry.split("T")[0]
            : "",
          empPoliceExpiry: currentValues.empPoliceExpiry
            ? currentValues.empPoliceExpiry.split("T")[0]
            : "",
          empAvcExpiry: currentValues.empAvcExpiry
            ? currentValues.empAvcExpiry.split("T")[0]
            : "",
          empLastInspection: currentValues.empLastInspection
            ? currentValues.empLastInspection.split("T")[0]
            : "",
          empInsuranceExpDate: currentValues.empInsuranceExpDate
            ? currentValues.empInsuranceExpDate.split("T")[0]
            : "",
          empMedicalIssueDate: currentValues.empMedicalIssueDate
            ? currentValues.empMedicalIssueDate.split("T")[0]
            : "",
          empMedicalExpiryDate: currentValues.empMedicalExpiryDate
            ? currentValues.empMedicalExpiryDate.split("T")[0]
            : currentValues.empMedicalIssueDate
              ? calculateMedicalExpiryDate(currentValues.empMedicalIssueDate)
              : "",
          empShiftStart: currentValues.empShiftStart || "08:00",
          empShiftEnd: currentValues.empShiftEnd || "17:00",
          empRouteEndType: currentValues.empRouteEndType || "depot",
          empEndLocation: getEndLocationId(currentValues.empEndLocation),
        });

        const category = currentValues?.empCategory;
        setStakeholderType(
          category === 3 || category === 4 ? "Customer" : "Transporter",
        );
      }
    }, [currentValues, editMode, defaultState, getEndLocationId]);

    // Recalculate age whenever birthdate changes
    useEffect(() => {
      if (employeeData.empBirthDate) {
        setAge(calculateAge(employeeData.empBirthDate));
      } else {
        setAge("");
      }
    }, [employeeData.empBirthDate, calculateAge]);

    // Imperative Handles for Parent Component
    useImperativeHandle(ref, () => ({
      getData: () => employeeData,
      validateForm: () => employeeValidation(employeeData),
      conditionalValidation: () => {
        return (
          (employeeData.empCategory === 1 || employeeData.empCategory === 2) &&
          !employeeData.empStakeholder
        );
      },
      isDeletedActive: () =>
        employeeData.empActive === 2 && initialActive !== 2,
      resetForm: () => {
        setEmployeeData(defaultState);
        setCurrentErrors({});
        setResetKey((prev) => prev + 1);
      },
    }));

    // --- Handlers ---
    const handleCategoryChange = (e, data) => {
      setEmployeeData((prev) => ({
        ...prev,
        empCategory: data.value,
        empStakeholder: null,
      }));
      if (Number(data.value) !== 1) {
        setCurrentErrors((prev) => {
          const {
            empLicenceNumber: _,
            empExpiryDate: __,
            empMedicalIssueDate: ___,
            ...rest
          } = prev;
          return rest;
        });
      }
      setStakeholderType(
        data.value === 3 || data.value === 4 ? "Customer" : "Transporter",
      );
    };

    const handleDateChange = (field, value) => {
      const today = new Date().toISOString().split("T")[0];
      if (value > today) {
        setCurrentErrors((prev) => ({
          ...prev,
          [field]: "Date cannot be in the future",
        }));
      } else {
        setCurrentErrors((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }
      setEmployeeData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    // Auto-generate username when 'Employee User' checkbox is toggled
    useEffect(() => {
      let isSubscribed = true;

      const generateUserName = async () => {
        if (!employeeData.empUserEnable || !employeeData.empFullName) {
          if (isSubscribed) {
            setEmployeeData((prev) => ({ ...prev, empUserName: "" }));
          }
          return;
        }

        let baseName = employeeData.empFullName
          .toLowerCase()
          .replace(/\s/g, "");
        let userName = baseName;
        let attempts = 0;

        try {
          while (await validateFieldUserName("username", userName, 2)) {
            attempts++;
            userName = `${baseName}EMP${Math.floor(
              Math.random() * (99 - 9 + 1) + 9,
            )}`;
            if (attempts > 5) break;
          }
          if (isSubscribed) {
            setEmployeeData((prev) => ({ ...prev, empUserName: userName }));
          }
        } catch (err) {
          console.error("Error validating username:", err);
        }
      };

      generateUserName();

      return () => {
        isSubscribed = false;
      };
    }, [employeeData.empUserEnable, employeeData.empFullName]);

    if (isLoading) {
      return <LoadingSpinner />;
    }

    return (
      <form
        className="ui form"
        style={{ overflowY: "auto", maxHeight: "72vh", paddingRight: "5px" }}
      >
        {/* Card 1: Basic Information */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "15px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "15px",
              paddingBottom: "8px",
              borderBottom: "1px solid #eee",
            }}
          >
            <User size={18} color="#2185d0" />
            <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#333" }}>
              Personal Details & Identification
            </h3>
          </div>

          <div className="two fields">
            <Input
              label="Employee Code"
              error={currentErrors.empCode}
              module={MODULES.Master}
              form={FORM}
              field="emp_code"
              editMode={editMode}
              name="employeeCode"
              value={employeeData.empCode || ""}
              placeholder="Auto Generated Code"
              disabled
            />
            <Input
              label="Reference"
              error={currentErrors.empReference}
              module={MODULES.Master}
              form={FORM}
              field="emp_reference"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empReference: _, ...rest } = prev;
                  return rest;
                })
              }
              name="reference"
              value={employeeData.empReference || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empReference: e.target.value,
                }))
              }
            />
          </div>

          <div className="two fields">
            <Input
              required
              label="Full Name"
              error={currentErrors.empFullName}
              module={MODULES.Master}
              form={FORM}
              field="emp_fullname"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empFullName: _, ...rest } = prev;
                  return rest;
                })
              }
              name="employeeFullName"
              value={employeeData.empFullName || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empFullName: e.target.value,
                  empUserEnable: false,
                }))
              }
            />
            <Input
              label="Common Name"
              error={currentErrors.empCommonName}
              module={MODULES.Master}
              form={FORM}
              field="emp_common_name"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empCommonName: _, ...rest } = prev;
                  return rest;
                })
              }
              name="employeeCommonName"
              value={employeeData.empCommonName || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empCommonName: e.target.value,
                }))
              }
            />
          </div>

          <div className="three fields">
            <Input
              required
              label="NIC"
              error={currentErrors.empNic}
              companyWise
              companyCol="emp_company"
              activeCol="emp_active"
              module={MODULES.Master}
              form={FORM}
              field="emp_nic"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empNic: _, ...rest } = prev;
                  return rest;
                })
              }
              name="employeeNIC"
              value={employeeData.empNic || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empNic: e.target.value,
                }))
              }
              duplicate={false}
              tableName="masterdata.tms_m_employee"
              primaryKey="emp_id"
              searchField="emp_nic"
              keyType="S"
              duplicateErrorMessage="This NIC already exists"
              setErrors={setCurrentErrors}
              errorField="empNic"
              id={employeeData.empId}
            />
            <Input
              required
              label="Birth Date"
              error={currentErrors.empBirthDate}
              module={MODULES.Master}
              form={FORM}
              field="emp_birth_date"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empBirthDate: _, ...rest } = prev;
                  return rest;
                })
              }
              type="date"
              name="empBirthDate"
              value={employeeData.empBirthDate || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empBirthDate: e.target.value,
                }))
              }
            />
            <Input
              label="Age (Years)"
              module={MODULES.Master}
              form={FORM}
              disabled
              field="emp_age"
              name="empAge"
              value={age}
            />
          </div>

          <div className="two fields">
            <Dropdown
              required
              label="Category"
              error={currentErrors.empCategory}
              module={MODULES.Master}
              form={FORM}
              field="emp_category"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empCategory: _, ...rest } = prev;
                  return rest;
                })
              }
              placeholder="Select Category"
              options={dropdown.empCategoryOpt || []}
              value={employeeData.empCategory || ""}
              onChange={handleCategoryChange}
            />
            <SearchDropdown
              key={`stakeholder-${resetKey}`}
              editMode={editMode}
              module={MODULES.Master}
              form={FORM}
              field="emp_stakeholder"
              reset={searchFieldReset}
              setReset={setSearchFieldReset}
              clearable
              onClear={() =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empStakeholder: null,
                }))
              }
              onResultSelect={(data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empStakeholder: data,
                }))
              }
              api={getOtherRefSearchDropdown}
              apiDependency={{
                type: "stakeholder_type",
                value: stakeholderType,
              }}
              fieldInfo={{
                tableName: "masterdata.tms_m_stakeholder",
                primaryKey: "stk_id",
                fieldName: "stk_name",
                id: employeeData.empStakeholder,
              }}
              error={currentErrors.empStakeholder}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empStakeholder: _, ...rest } = prev;
                  return rest;
                })
              }
              label="Stakeholder"
              required={
                employeeData.empCategory === 1 || employeeData.empCategory === 2
              }
            />
          </div>
        </div>

        {/* Card 2: Licensing & Divisional Verification */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "15px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "15px",
              paddingBottom: "8px",
              borderBottom: "1px solid #eee",
            }}
          >
            <Shield size={18} color="#2185d0" />
            <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#333" }}>
              Licensing & Divisional Governance
            </h3>
          </div>

          <div className="two fields">
            <Input
              required={Number(employeeData.empCategory) === 1}
              label="Licence Number"
              error={currentErrors.empLicenceNumber}
              module={MODULES.Master}
              form={FORM}
              field="emp_license_number"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empLicenceNumber: _, ...rest } = prev;
                  return rest;
                })
              }
              name="employeeLicenceNumber"
              value={employeeData.empLicenceNumber || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empLicenceNumber: e.target.value,
                }))
              }
            />
            <Input
              required={Number(employeeData.empCategory) === 1}
              label="Licence Expiry Date"
              error={currentErrors.empExpiryDate}
              module={MODULES.Master}
              form={FORM}
              field="emp_expiary_date"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empExpiryDate: _, ...rest } = prev;
                  return rest;
                })
              }
              type="date"
              name="empExpiaryDate"
              value={employeeData.empExpiryDate || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empExpiryDate: e.target.value,
                }))
              }
            />
          </div>

          {Number(employeeData.empCategory) === 1 &&
            age !== "" &&
            Number(age) >= 60 &&
            Number(employeeData.empAuthorized) !== 4 && (
              <div className="two fields">
                <Input
                  required
                  label="Medical Certificate Issue Date"
                  error={currentErrors.empMedicalIssueDate}
                  module={MODULES.Master}
                  form={FORM}
                  field="emp_medical_issue_date"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prev) => {
                      const { empMedicalIssueDate: _, ...rest } = prev;
                      return rest;
                    })
                  }
                  type="date"
                  name="empMedicalIssueDate"
                  value={employeeData.empMedicalIssueDate || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    const calculatedExpiry = calculateMedicalExpiryDate(value);
                    const today = new Date().toISOString().split("T")[0];

                    setEmployeeData((prev) => ({
                      ...prev,
                      empMedicalIssueDate: value,
                      empMedicalExpiryDate: calculatedExpiry,
                    }));

                    if (!value) {
                      setCurrentErrors((prev) => ({
                        ...prev,
                        empMedicalIssueDate:
                          "Medical Certificate Issue Date is required for drivers aged 60 and above",
                      }));
                    } else if (value > today) {
                      setCurrentErrors((prev) => ({
                        ...prev,
                        empMedicalIssueDate:
                          "Medical Certificate Issue Date cannot be in the future",
                      }));
                    } else if (!isMedicalCertificateValid(value)) {
                      setCurrentErrors((prev) => ({
                        ...prev,
                        empMedicalIssueDate:
                          "Medical Certificate has expired (validity is 2 years from issue date)",
                      }));
                    } else {
                      setCurrentErrors((prev) => {
                        const { empMedicalIssueDate: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                />
                <Input
                  label="Medical Certificate Expiry Date"
                  module={MODULES.Master}
                  form={FORM}
                  field="emp_medical_expiry_date"
                  disabled
                  type="date"
                  name="empMedicalExpiryDate"
                  value={employeeData.empMedicalExpiryDate || ""}
                  placeholder="Auto-calculated (Issue Date + 2 Years)"
                />
              </div>
            )}

          <div className="two fields">
            <SearchDropdown
              key={`gs-${resetKey}`}
              editMode={editMode}
              module={MODULES.Master}
              form={FORM}
              field="emp_gs_division"
              reset={searchFieldReset}
              setReset={setSearchFieldReset}
              clearable
              onClear={() =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empGsDivision: "",
                }))
              }
              onResultSelect={(data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empGsDivision: data,
                }))
              }
              api={gsDivStartsWith}
              apiDependency={{ companyId: company }}
              fieldInfo={{
                tableName: "masterdata.tms_vm_reg_gs",
                primaryKey: "rgs_id",
                fieldName: "rgs_name",
                id: employeeData.empGsDivision,
              }}
              label="GS Division"
              error={currentErrors.empGsDivision}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empGsDivision: _, ...rest } = prev;
                  return rest;
                })
              }
            />
            <Input
              label="GS Expiry Date"
              error={currentErrors.empGsExpiry}
              module={MODULES.Master}
              form={FORM}
              field="emp_gs_expiry"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empGsExpiry: _, ...rest } = prev;
                  return rest;
                })
              }
              type="date"
              name="empGsExpiryDate"
              value={employeeData.empGsExpiry || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empGsExpiry: e.target.value,
                }))
              }
            />
          </div>

          <div className="two fields">
            <SearchDropdown
              key={`police-${resetKey}`}
              editMode={editMode}
              module={MODULES.Master}
              form={FORM}
              field="emp_police_division"
              reset={searchFieldReset}
              setReset={setSearchFieldReset}
              clearable
              onClear={() =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empPoliceDivision: "",
                }))
              }
              onResultSelect={(data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empPoliceDivision: data,
                }))
              }
              api={policeDivStartsWith}
              fieldInfo={{
                tableName: "masterdata.tms_vm_reg_police_division",
                primaryKey: "rpo_id",
                fieldName: "rpo_name",
                id: employeeData.empPoliceDivision,
              }}
              label="Police Division"
              error={currentErrors.empPoliceDivision}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empPoliceDivision: _, ...rest } = prev;
                  return rest;
                })
              }
            />
            <Input
              label="Police Expiry Date"
              error={currentErrors.empPoliceExpiry}
              module={MODULES.Master}
              form={FORM}
              field="emp_police_expiry"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empPoliceExpiry: _, ...rest } = prev;
                  return rest;
                })
              }
              type="date"
              name="empPoliceExpiryDate"
              value={employeeData.empPoliceExpiry || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empPoliceExpiry: e.target.value,
                }))
              }
            />
          </div>

          <div className="three fields">
            <Dropdown
              required
              label="Authorized"
              error={currentErrors.empAuthorized}
              module={MODULES.Master}
              form={FORM}
              field="emp_authorized"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empAuthorized: _, ...rest } = prev;
                  return rest;
                })
              }
              placeholder="Authorized"
              options={dropdown.empAuthorizeOpt || []}
              value={employeeData.empAuthorized || ""}
              onChange={(e, data) => {
                setEmployeeData((prev) => ({
                  ...prev,
                  empAuthorized: data.value,
                }));
                if (Number(data.value) === 4) {
                  setCurrentErrors((prev) => {
                    const { empMedicalIssueDate: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
            />

            <Dropdown
              required
              label="ACV"
              error={currentErrors.empAvc}
              module={MODULES.Master}
              form={FORM}
              field="emp_acv"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empAvc: _, ...rest } = prev;
                  return rest;
                })
              }
              placeholder="ACV"
              options={dropdown.empAcvOpt || []}
              value={employeeData.empAvc || ""}
              onChange={(e, data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empAvc: data.value,
                }))
              }
            />

            <Input
              label="ACV Expiry Date"
              error={currentErrors.empAvcExpiry}
              module={MODULES.Master}
              form={FORM}
              field="emp_acv_expiry"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empAvcExpiry: _, ...rest } = prev;
                  return rest;
                })
              }
              type="date"
              name="empAcvExpiryDate"
              value={employeeData.empAvcExpiry || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empAvcExpiry: e.target.value,
                }))
              }
            />
          </div>
        </div>

        {/* Card 3: Contact & Insurance */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "15px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "15px",
              paddingBottom: "8px",
              borderBottom: "1px solid #eee",
            }}
          >
            <Briefcase size={18} color="#2185d0" />
            <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#333" }}>
              Contact, Operations & Insurance
            </h3>
          </div>

          <div className="two fields">
            <Input
              label="Phone"
              error={currentErrors.empPhone}
              module={MODULES.Master}
              form={FORM}
              field="emp_phone"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empPhone: _, ...rest } = prev;
                  return rest;
                })
              }
              name="empPhone"
              value={employeeData.empPhone || ""}
              minLength={10}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empPhone: e.target.value,
                }))
              }
            />
            <Input
              required
              label="Mobile"
              error={currentErrors.empMobile}
              module={MODULES.Master}
              form={FORM}
              field="emp_mobile"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empMobile: _, ...rest } = prev;
                  return rest;
                })
              }
              name="empMobile"
              value={employeeData.empMobile || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empMobile: e.target.value,
                }))
              }
            />
          </div>

          <div className="two fields">
            <Input
              label="Email"
              companyWise
              companyCol="emp_company"
              activeCol="emp_active"
              error={currentErrors.empEmail}
              module={MODULES.Master}
              form={FORM}
              field="emp_email"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empEmail: _, ...rest } = prev;
                  return rest;
                })
              }
              type="email"
              name="empEmail"
              value={employeeData.empEmail || ""}
              onChange={(e) => {
                const value = e.target.value;
                setEmployeeData((prev) => ({ ...prev, empEmail: value }));

                if (employeeData.empUserEnable && !value) {
                  setCurrentErrors((prev) => ({
                    ...prev,
                    empEmail: "Email is required",
                  }));
                } else {
                  setCurrentErrors((prev) => {
                    const { empEmail, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              duplicate={false}
              tableName="masterdata.tms_m_employee"
              primaryKey="emp_id"
              searchField="emp_email"
              keyType="S"
              duplicateErrorMessage="This email already exists"
              setErrors={setCurrentErrors}
              errorField="empEmail"
              id={employeeData.empId}
              required={employeeData.empUserEnable}
            />
            <Dropdown
              label="Insurance Coverage"
              error={currentErrors.empInsuranceCoverage}
              module={MODULES.Master}
              form={FORM}
              clearable
              field="emp_insurance_coverage"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empInsuranceCoverage: _, ...rest } = prev;
                  return rest;
                })
              }
              onClear={() =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empInsuranceCoverage: -1,
                }))
              }
              placeholder="Select Coverage"
              options={dropdown.empInCoverageOpt || []}
              value={employeeData.empInsuranceCoverage}
              onChange={(e, data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empInsuranceCoverage: data.value,
                }))
              }
            />
          </div>

          <div className="two fields">
            <Input
              label="Insurance Expire Date"
              error={currentErrors.empInsuranceExpDate}
              module={MODULES.Master}
              form={FORM}
              field="emp_insurance_exp_date"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empInsuranceExpDate: _, ...rest } = prev;
                  return rest;
                })
              }
              type="date"
              name="empInsuranceExpDate"
              value={employeeData.empInsuranceExpDate?.split("T")[0] || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empInsuranceExpDate: e.target.value,
                }))
              }
            />
            <SearchDropdown
              key={`insurance-${resetKey}`}
              editMode={editMode}
              module={MODULES.Master}
              form={FORM}
              field="emp_insurance_provider"
              reset={searchFieldReset}
              setReset={setSearchFieldReset}
              clearable
              onClear={() =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empInsuranceProvider: -1,
                }))
              }
              onResultSelect={(data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empInsuranceProvider: data,
                }))
              }
              api={getOtherRefSearchDropdown}
              apiDependency={{
                type: "stakeholder_type",
                value: "Supplier",
                param1: "Insurance Provider",
              }}
              fieldInfo={{
                tableName: "masterdata.tms_m_stakeholder",
                primaryKey: "stk_id",
                fieldName: "stk_name",
                id: employeeData.empInsuranceProvider,
              }}
              error={currentErrors.empInsuranceProvider}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empInsuranceProvider: _, ...rest } = prev;
                  return rest;
                })
              }
              label="Insurance Provider"
            />
          </div>

          <div className="two fields">
            <Input
              label="Remarks"
              error={currentErrors.empRemarks}
              module={MODULES.Master}
              form={FORM}
              field="emp_remarks"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empRemarks: _, ...rest } = prev;
                  return rest;
                })
              }
              name="empRemarks"
              value={employeeData.empRemarks || ""}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empRemarks: e.target.value,
                }))
              }
            />
            <Input
              label="Last Inspection Date"
              error={currentErrors.empLastInspection}
              module={MODULES.Master}
              form={FORM}
              field="emp_last_inspection"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empLastInspection: _, ...rest } = prev;
                  return rest;
                })
              }
              type="date"
              name="empLastInspectionDate"
              value={employeeData.empLastInspection || ""}
              onChange={(e) =>
                handleDateChange("empLastInspection", e.target.value)
              }
            />
          </div>
        </div>

        {/* Card 4: Shift & Route Preference Settings */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "15px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "15px",
              paddingBottom: "8px",
              borderBottom: "1px solid #eee",
            }}
          >
            <Clock size={18} color="#2185d0" />
            <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#333" }}>
              Shift & Route End Preferences
            </h3>
          </div>

          <div className="two fields">
            <Input
              label="Shift Start Time"
              type="time"
              module={MODULES.Master}
              form={FORM}
              field="emp_shift_start"
              editMode={editMode}
              name="empShiftStart"
              value={employeeData.empShiftStart || "08:00"}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empShiftStart: e.target.value,
                }))
              }
            />
            <Input
              label="Shift End Time"
              type="time"
              module={MODULES.Master}
              form={FORM}
              field="emp_shift_end"
              editMode={editMode}
              name="empShiftEnd"
              value={employeeData.empShiftEnd || "17:00"}
              onChange={(e) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empShiftEnd: e.target.value,
                }))
              }
            />
          </div>

          <div className="two fields">
            <Dropdown
              label="Route End Preference"
              module={MODULES.Master}
              form={FORM}
              field="emp_route_end_type"
              editMode={editMode}
              placeholder="Select End Behavior"
              options={[
                { key: 1, text: "Return to Depot", value: "depot" },
                { key: 2, text: "End at Last Delivery Stop", value: "any" },
                {
                  key: 3,
                  text: "Custom Address (e.g., Driver Home)",
                  value: "custom",
                },
              ]}
              value={employeeData.empRouteEndType || "depot"}
              onChange={(e, data) => {
                setEmployeeData((prev) => ({
                  ...prev,
                  empRouteEndType: data.value,
                  ...(data.value !== "custom" && { empEndLocation: null }),
                }));
              }}
            />

            {employeeData.empRouteEndType === "custom" && (
              <SearchDropdown
                key={`custom_end_location-${resetKey}`}
                label="Custom End Location"
                editMode={editMode}
                module={MODULES.Master}
                form={FORM}
                field="emp_end_location"
                reset={searchFieldReset}
                setReset={setSearchFieldReset}
                clearable
                onClear={() =>
                  setEmployeeData((prev) => ({
                    ...prev,
                    empEndLocation: null,
                  }))
                }
                onResultSelect={(data) => {
                  const locId =
                    typeof data === "object"
                      ? data.locId || data.value || data
                      : data;
                  setEmployeeData((prev) => ({
                    ...prev,
                    empEndLocation: locId,
                  }));
                }}
                api={getOtherRefDetails}
                apiDependency={{
                  type: "location",
                  value: "",
                  param3: 2,
                }}
                fieldInfo={{
                  tableName: "masterdata.tms_m_locations",
                  primaryKey: "loc_id",
                  fieldName: "loc_name",
                  id: employeeData.empEndLocation,
                }}
                error={currentErrors.empEndLocation}
                resetError={() =>
                  setCurrentErrors((prev) => {
                    const { empEndLocation: _, ...rest } = prev;
                    return rest;
                  })
                }
              />
            )}
          </div>
        </div>

        {/* Card 5: User Account Provisioning (New Creation Only) */}
        {!editMode && (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #d4d4d5",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "15px",
            }}
          >
            <h4
              className="ui header"
              style={{ margin: "0 0 15px 0", color: "#2185d0" }}
            >
              System User Account Setup
            </h4>
            <div className="two fields">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CheckBox
                  label="Enable Employee User Account"
                  checked={employeeData.empUserEnable}
                  onChange={() =>
                    setEmployeeData((prev) => ({
                      ...prev,
                      empUserEnable: !prev.empUserEnable,
                    }))
                  }
                  disabled={!employeeData.empFullName || editMode}
                />
              </div>

              <Input
                label="User Name"
                error={currentErrors.empUserName}
                module={MODULES.Master}
                form={FORM}
                field="emp_username"
                placeholder={
                  employeeData.empUserEnable
                    ? "Generating Username..."
                    : "Enter User Name"
                }
                editMode={editMode}
                resetError={() =>
                  setCurrentErrors((prev) => {
                    const { empUserName: _, ...rest } = prev;
                    return rest;
                  })
                }
                name="employeeUserName"
                value={employeeData.empUserName || ""}
                onChange={(e) =>
                  setEmployeeData((prev) => ({
                    ...prev,
                    empUserName: e.target.value,
                  }))
                }
                disabled={
                  !employeeData.empUserEnable || !employeeData.empFullName
                }
              />
            </div>
          </div>
        )}

        {/* Card 6: Operational & Active Status */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "15px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div className="two fields">
            <Dropdown
              required
              label="Status"
              error={currentErrors.empStatus}
              module={MODULES.Master}
              form={FORM}
              field="emp_status"
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empStatus: _, ...rest } = prev;
                  return rest;
                })
              }
              placeholder="Select Status"
              options={dropdown.specialStatusOpt || []}
              value={employeeData.empStatus}
              onChange={(e, data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empStatus: data.value,
                }))
              }
            />
            <Dropdown
              required
              label="Active Status"
              error={currentErrors.empActive}
              module={MODULES.Master}
              form={FORM}
              field="emp_active"
              authenticated
              needDeletePermission
              editMode={editMode}
              resetError={() =>
                setCurrentErrors((prev) => {
                  const { empActive: _, ...rest } = prev;
                  return rest;
                })
              }
              placeholder="Select Active Status"
              options={dropdown.empActiveOpt || []}
              value={employeeData.empActive}
              onChange={(e, data) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  empActive: data.value,
                }))
              }
            />
          </div>
        </div>

        {/* Audit Trail Card (Edit Mode) */}
        {editMode && (
          <div style={{ marginTop: "15px" }}>
            <InfoList
              title="User Information & Audit Log"
              content={[
                {
                  title: "Created By",
                  value: employeeData.createdBy
                    ? employeeData.createdBy
                    : "N/A",
                },
                {
                  title: "Created Date",
                  value: employeeData.createdDate
                    ? formatFromJsonDate(employeeData.createdDate)
                    : "N/A",
                },
                {
                  title: "Last Modified By",
                  value: employeeData.lastModifiedBy
                    ? employeeData.lastModifiedBy
                    : "N/A",
                },
                {
                  title: "Last Modified Date",
                  value: employeeData.lastModifiedDate
                    ? formatFromJsonDate(employeeData.lastModifiedDate)
                    : "N/A",
                },
              ]}
              Icon={User}
            />
          </div>
        )}
      </form>
    );
  },
);

export default EmployeeComponent;
